import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TaxEditComponent } from 'app/common/tax/tax-edit.component';
import { GeneralSettings } from 'app/profile/models/generic.model';
import { Country } from 'app/profile/models/masterdata.model';
import { OnboardingDocument, OnboardingTab, TaskStatus } from 'app/profile/models/onboarding.model';
import { ContextVerbiage, Currency, OPERATION, Permission, SupplierTax } from 'app/profile/models/supplier.model';
import { SupplierTaxInfo, TaxCountryConfig, TaxIDType } from 'app/profile/models/tax.model';
import { OnboardingDocumentsService } from 'app/profile/services/onboarding-document.service';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { TaxState } from 'app/profile/store/state/tax.state';
import { Observable, forkJoin } from 'rxjs';
import * as MasterDataActions from "../../../store/actions/masterdata.action";
import * as MasterDataSelectors from "../../../store/selectors/masterdata.selector";
import * as TaxActions from "../../../store/actions/tax.actions";
import * as TaxSelectors from "../../../store/selectors/tax.selectors";
import * as GeneralActions from "../../../store/actions/general.action";
import * as GeneralSelectors from "../../../store/selectors/general.selector";
import * as OnboardingActions from '../../../store/actions/onboarding.actions';
import * as OnboardingSelectors from '../../../store/selectors/onboarding.selectors';
import { OnboardingState } from 'app/profile/store/state/onboarding.state';
import { ActivatedRoute, Router } from '@angular/router';
import { TslToastrService } from '@tesla/ng-tds';
import { SupplierTaskEntityMapping, SupplierTaskEntityMappingOption, SupplierTaskResponse, SupplierTaskStep, SupplierTaskTableCol, SupplierTaskTableMasterData } from 'app/profile/models/suppliertasks.model';
import { SupplierTaskService } from 'app/profile/services/supplier-task.service';
import { getSupplierTax, getSupplierTaxInfoValues, isNotNullOrUndefined, isNullOrEmpty } from 'app/shared/utils/utils';
import { cloneDeep } from "lodash";
import { ExistSelectComponent } from '../exist-select/exist-select.component';
import { MatDialog } from '@angular/material/dialog';
import { TaxTaskDialogComponent } from './tax-task-dialog/tax-task-dialog.component';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { isNullOrUndefined } from 'util';
import { I18nService } from 'app/shared/services/i18n.service';
import { EntityMappingComponent } from '../entity-mapping/entity-mapping.component';

@Component({
  selector: 'app-tax-task-wrapper',
  templateUrl: './tax-task-wrapper.component.html',
  styleUrls: ['./tax-task-wrapper.component.scss']
})
export class TaxTaskWrapperComponent implements OnInit {
  onboardingTab: OnboardingTab = OnboardingTab.TAX;
  supplierCode: string = '';
  taskId: number;
  taskStatus: string;
  requestType: string;

  //  Fields required by the tax-edit form
  countries$: Observable<Country[]>;
  taxCountries$: Observable<TaxCountryConfig[]>;
  currencies$: Observable<Currency[]>;
  taxIdTypes$: Observable<TaxIDType[]>;
  settings$: Observable<GeneralSettings>;
  supplierTax$: Observable<SupplierTaxInfo>;
  supplierTax: SupplierTaxInfo;
  supplierTaxList: SupplierTaxInfo[];
  taskInfo: SupplierTaskStep;
  requiredDocuments: OnboardingDocument[];
  contextVerbiage: ContextVerbiage;
  isLoaded: boolean = false;

  supplierTaxDocType: string[] = ['W-2', '1099', 'RFC', 'MXCOMP', 'CORR-T', 'TI', 'W-8'];
  taxDocuments: any;
  rejectionDetails: any;

  // reactivation
  selectedIndexList: number[] = [];
  unSelectedIndexList: number[] = [];
  primaryIndex: number;
  existingItemList: SupplierTax[];
  displayedColumns: SupplierTaskTableCol[] = [];
  tableData: SupplierTaskTableMasterData;
  isValidSelectTable: boolean = true;
  isCreateNew: boolean = false;
  tableErrorMessage: string;
  entityMappings: SupplierTaskEntityMapping[] = [];
  entityMappingOptions: SupplierTaskEntityMappingOption[] = [];
  
  //  Reference to the child component so that we can invoke the onSubmit action
  @ViewChild('taxEditComponent', { static: false }) taxEditComponent: TaxEditComponent;
  @ViewChild('selectTable', { static: false }) selectTableComponent: ExistSelectComponent;
  @ViewChild('entityMapping', { static: false }) entityMappingComponent: EntityMappingComponent;
  
  @Input('supplierCode') set _supplierCode(val: string) { if (val && val.trim() != "") this.supplierCode = val; };
  @Input('taskId') set _taskId(val: number) { if (val) this.taskId = val; };
  @Input('isSidePanelMode') isSidePanelMode: boolean = false;
  @Input() isReactivation: boolean = false;
  @Input() enableExistSelection: boolean = false;
  @Output() onSave: EventEmitter<boolean> = new EventEmitter<boolean>();
  
  constructor(private onboardingDocumentsService: OnboardingDocumentsService,
    private supplierStore: Store<SupplierProfileState>,
    private taxStore: Store<TaxState>,
    private onboardingStore: Store<OnboardingState>,
    private route: ActivatedRoute,
    private toaster: TslToastrService,
    private taskService: SupplierTaskService,
    private router: Router,
    private matDialog: MatDialog,
    private fileUploadService: FileUploadService,
    private translate: I18nService,
    private supplierStateService: SupplierStateService,
  ) { }

  ngOnInit(): void {
    this.route?.parent?.params.subscribe(params => {
      this.supplierCode = params.code;
      this.taskId = params.portalTaskId;
    });

    this.route.data.subscribe(data => {
      if(data && isNotNullOrUndefined(data.isReactivation)){
        this.isReactivation = data.isReactivation;
      }
      if(data && isNotNullOrUndefined(data.enableExistSelection)){
        this.enableExistSelection = data.enableExistSelection;
      }
    });

    // Dispatch to get documentTypes
    this.onboardingDocumentsService.getDocumentTypes().subscribe(res => {
      if (res) {
        this.taxDocuments = res.filter(x => x.category == "Tax");

        this.taxDocuments?.forEach(doc => {
          if (!this.supplierTaxDocType.includes(doc.name)) {
            this.supplierTaxDocType.push(doc.name);
          }
        })
      }
    });

    // get task details by task type.
    this.taskService.getSupplierTaskStep(this.supplierCode, this.taskId, this.onboardingTab).subscribe(taskInfo => {
      if (taskInfo && taskInfo.taskConfiguration) {
        this.taskInfo = taskInfo;
        this.requestType = this.taskInfo?.taskConfiguration?.SupplierRequestType;
        if(this.requestType) this.requestType = this.requestType[0].toLocaleLowerCase() + this.requestType.substring(1);
        if (!taskInfo.portalTaskResponse?.DocumentInfos?.length) this.requiredDocuments = taskInfo.taskConfiguration?.Documents;
        else this.requiredDocuments = taskInfo.portalTaskResponse?.DocumentInfos?.filter(d => d.DocumentType && this.supplierTaxDocType.includes(d.DocumentType.toUpperCase()));
        this.taskStatus = taskInfo.taskStatus;
        this.rejectionDetails = taskInfo?.rejectionDetails;
        if (this.taskStatus != TaskStatus.Completed && taskInfo.portalTaskResponse != null) {
          if(this.isReactivation || this.enableExistSelection || ["nameOrTINChange"].includes(this.requestType)) {
            if(!taskInfo.portalTaskResponse.length)this.supplierTaxList = [taskInfo.portalTaskResponse];
            else {
              this.supplierTaxList = taskInfo.portalTaskResponse.filter(x => {
              if(["nameOrTINChange"].includes(this.requestType)) return x.operation != OPERATION.DELETE;
              else return x.IsValid;
            });
              this.requiredDocuments = this.supplierTaxList.find(x => x.ID == 0)?.DocumentInfos;
            }
          } else this.supplierTax = taskInfo.portalTaskResponse;
        }
        if(this.isReactivation || this.enableExistSelection || ["nameOrTINChange"].includes(this.requestType)) {
          // use supplier code and based on type to grab item list
          this.supplierStateService.getTaxListByCode(this.supplierCode).subscribe(resList => {
            this.existingItemList = resList?.map(x => getSupplierTax(x));
            this.existingItemList.forEach(x => {
              if(!x.DocumentInfos?.length) x.DocumentInfos = cloneDeep(this.requiredDocuments)
            });
            this.initTableData();
            if(["nameOrTINChange"].includes(this.requestType)) {
              this.supplierStateService.getEntityMappingByCodeAndTaskType(this.supplierCode, "TaxList").subscribe(res => {
                if(this.supplierTaxList?.length) {
                  res?.forEach(x => {
                    x.taskItem = this.existingItemList.find(y => y.EntityCountryIDList?.includes(x.entityCountryID))?.ID;
                  });
                }
                this.entityMappings = res;
                setTimeout(() => {
                  this.initEntityMappingOptionData();
                }, 0);
              });
            }
          })
        } else this.isLoaded = true;
      }
    });

    //  Dispatch action to get general settings, countries, currencies and tax id types
    this.supplierStore.dispatch(new GeneralActions.GetGeneralSettings());
    this.supplierStore.dispatch(new MasterDataActions.GetCurrency());
    this.taxStore.dispatch(TaxActions.GetTaxCountries());
    this.taxStore.dispatch(TaxActions.GetTaxIDTypes());

    //  Get selectors to query the required data for the tax-edit component
    this.settings$ = this.supplierStore.select(GeneralSelectors.getGeneralSettings);
    this.currencies$ = this.supplierStore.select(MasterDataSelectors.SelectCurrencies);
    this.taxCountries$ = this.supplierStore.select(TaxSelectors.GetTaxCountries);
    this.taxIdTypes$ = this.taxStore.select(TaxSelectors.GetTaxIDTypes);
    this.onboardingStore.select(MasterDataSelectors.SelectContextVerbiage).subscribe(contextVerbiage => this.contextVerbiage = contextVerbiage);
  }

  initTableData() {
    this.displayedColumns = [
      {propertyName: "taxCountry", title: "Tax Country"},
      {propertyName: "taxName", title: "Tax Name"},
      {propertyName: "taxType", title: "Tax Type"},
      {propertyName: "federalTaxID", title: "Tax ID"},
      {propertyName: "vatNumber", title: "VAT Number"},
      {propertyName: "documents", title: "Documents"}
    ];
    let existIndexList = [];
    if(!this.existingItemList.length) this.existingItemList = [];
    if(!this.selectedIndexList) this.selectedIndexList = [];
    if(!this.unSelectedIndexList) this.unSelectedIndexList = [];

    if(["nameOrTINChange"].includes(this.requestType)) {
      this.supplierTaxList?.forEach(supplierTax => {
        if(supplierTax) {
          let existIndex = this.existingItemList?.findIndex(x => x.ID == supplierTax.ID && supplierTax.ID != 0);
          if(existIndex < 0) {
            supplierTax.Operation = OPERATION.CREATE;
            supplierTax.ID = Math.floor(1000000 + Math.random() * 9000000);
            existIndexList.push(this.existingItemList.push(cloneDeep(supplierTax)) - 1);
          } else {
            this.existingItemList[existIndex] = cloneDeep(supplierTax);
            if(supplierTax.Operation !== OPERATION.DELETE) existIndexList.push(existIndex)
          }
        }
      });
      let selectHeader = "Please confirm if this information is still active.";
      this.tableData = {
        supplierCode: this.supplierCode,
        onboardingTab: this.onboardingTab,
        selectHeader: selectHeader,
        countryName: this.taskInfo?.taskConfiguration?.EntityCountry,
        disableCreateNew: false,
        enableFirstEdit: true,
        disableEdit: false,
        enableMultiSelect: true,
        enablePrimarySelect: false,
        enableDocumentUpload: false,
        showMessage: true,
        enableAddNew: true,
        displayTitle: false,
        createNewLabel: "ADD NEW TAX INFORMATION",
        data: this.existingItemList?.map((x, index) => {
          let obj = {
            taxCountry: x.TaxCountry,
            taxName: x.TaxName,
            taxType: x.TaxIDTypes,
            federalTaxID: !isNullOrUndefined(x.Last4FederalTaxID) && !isNullOrEmpty(x.Last4FederalTaxID) ? 'xxxx-'+x.Last4FederalTaxID : x.FederalTaxID,
            vatNumber: x.VATNumber,
            document: x.Documents?.join(", "),
            value: x.ID,
            documentInfos: x.DocumentInfos?.length? x.DocumentInfos: cloneDeep(this.requiredDocuments),
            isNew: x.Operation == OPERATION.CREATE
          };
          if(existIndexList?.includes(index)) {
            obj["isSelected"] = true;
            this.isValidSelectTable = true;
            this.selectedIndexList.push(index);
          } else if(existIndexList?.length || (!!(this.supplierTaxList?.length  && !existIndexList?.length))) {
            this.unSelectedIndexList.push(index);
            obj["isSelected"] = false;
          }
          return obj;
        })
      };
    } else {
      if(!this.existingItemList?.length) {
        this.isValidSelectTable = true;
        this.isCreateNew = true;
      }
      this.supplierTaxList?.forEach(supplierTax => {
        if(supplierTax) {
          let existIndex = this.existingItemList?.findIndex(x => x.ID == supplierTax.ID);
          if(existIndex < 0) {
            if(this.existingItemList.length) {
              this.selectedIndexList.push(this.existingItemList.length);
            }
            this.isValidSelectTable = true;
            this.isCreateNew = true;
            this.supplierTax = supplierTax;
          } else {
            this.existingItemList[existIndex] = cloneDeep(supplierTax);
            existIndexList.push(existIndex)
          }
        }
      });

      let selectHeader;
      if(this.requestType == "entityExtension") selectHeader = "Please confirm if this information can be used to conduct business with the listed Tesla location or add new details.";
      else selectHeader = "Please confirm if this information is still active.";
      
      this.tableData = {
        supplierCode: this.supplierCode,
        onboardingTab: this.onboardingTab,
        selectHeader: selectHeader,
        countryName: this.taskInfo?.taskConfiguration?.EntityCountry,
        disableCreateNew: false,
        enableFirstEdit: true,
        disableEdit: false,
        isNewSelected: this.supplierTaxList?.length && existIndexList?.length == 0,
        enableMultiSelect: this.isReactivation,
        enablePrimarySelect: this.isReactivation,
        displayTitle: true,
        enableDocumentUpload: this.requestType?.toLocaleLowerCase() == "entityextension" ? false : true,
        showMessage: this.isReactivation,
        data: this.existingItemList?.map((x, index) => {
          let obj = {
            taxCountry: x.TaxCountry,
            taxName: x.TaxName,
            taxType: x.TaxIDTypes,
            federalTaxID: !isNullOrUndefined(x.Last4FederalTaxID) && !isNullOrEmpty(x.Last4FederalTaxID) ? 'xxxx-'+x.Last4FederalTaxID : x.FederalTaxID,
            vatNumber: x.VATNumber,
            document: x.Documents?.join(", "),
            value: x.ID,
            isPrimary: x.IsPrimary,
            documentInfos: x.DocumentInfos?.length? x.DocumentInfos: cloneDeep(this.requiredDocuments)
          };
          if(obj.isPrimary) this.primaryIndex = index;
          if(existIndexList?.includes(index)) {
            obj["isSelected"] = true;
            this.isValidSelectTable = true;
            this.selectedIndexList.push(index);
          } else if(existIndexList?.length || (!!(this.supplierTaxList?.length  && !existIndexList?.length))) {
            obj["isSelected"] = false;
            if(!this.unSelectedIndexList) this.unSelectedIndexList = [];
            this.unSelectedIndexList.push(index);
          }
          return obj;
        })
      };
    }
    this.isLoaded = true;
  }

  initEntityMappingOptionData() {
    this.entityMappingOptions = this.existingItemList?.filter((x, index) => this.selectedIndexList?.includes(index))?.map(x => {
      return {
        displayName: x.TaxName,
        value: x.ID
      }
    })
  }

  onSelected(index) {
    this.isValidSelectTable = true;
    if(["nameOrTINChange"].includes(this.requestType)) {
      if(!this.selectedIndexList) this.selectedIndexList = [];
      if(!this.unSelectedIndexList) this.unSelectedIndexList = [];
      if(index < 0) {
        if(this.selectedIndexList.includes(-(index + 1))) {
          index = -(index + 1);
          this.selectedIndexList.splice(this.selectedIndexList.indexOf(index), 1);
          this.unSelectedIndexList.push(index);
          this.initEntityMappingOptionData();
        } else this.unSelectedIndexList.push(-(index + 1));
      } else {  
        if(this.selectedIndexList.includes(this.existingItemList?.length)) {
          this.selectedIndexList.splice(this.selectedIndexList.indexOf(this.existingItemList?.length), 1);
          this.initEntityMappingOptionData();
        }
        this.selectedIndexList.push(index);
        if(this.unSelectedIndexList.includes(index)) this.unSelectedIndexList.splice(this.unSelectedIndexList.indexOf(index), 1);
      }
    } else {
      if(this.isReactivation) {
        if(!this.selectedIndexList) this.selectedIndexList = [];
        if(!this.unSelectedIndexList) this.unSelectedIndexList = [];
        if(index < 0) {
          if(this.selectedIndexList.includes(-(index + 1))) {
            index = -(index + 1);
            this.selectedIndexList.splice(this.selectedIndexList.indexOf(index), 1);
            this.unSelectedIndexList.push(index);
          }
        } else if(index == this.existingItemList?.length) {
          this.selectedIndexList = [this.existingItemList?.length];
          this.isCreateNew = true;
          this.supplierTax = new SupplierTaxInfo();
        } else {  
          if(this.selectedIndexList.includes(this.existingItemList?.length)) this.selectedIndexList.splice(this.selectedIndexList.indexOf(this.existingItemList?.length), 1);
          this.selectedIndexList.push(index);
          if(this.unSelectedIndexList.includes(index)) this.unSelectedIndexList.splice(this.unSelectedIndexList.indexOf(index), 1);
          this.isCreateNew = false;
        }
      } else {
        this.selectedIndexList = [index];
        if(this.selectedIndexList[0] >= this.existingItemList?.length) {
          this.isCreateNew = true;
          this.supplierTax = new SupplierTaxInfo();
        }
        else this.isCreateNew = false;
      }
    }
  }

  onPrimarySelected(index) {
    this.primaryIndex = index;
  }

  onEdit(event) {
    let index = event?.index;
    if(index < this.existingItemList?.length) {
      const dialogRef = this.matDialog.open(TaxTaskDialogComponent,
        { 
          width: '1100px', 
          maxHeight: "80vh",
          autoFocus: false,
          data: { 
            supplierCode: this.supplierCode, 
            requiredDocuments: this.existingItemList[index]?.DocumentInfos?.filter(d => d.DocumentType && this.supplierTaxDocType.includes(d.DocumentType.toUpperCase())) || this.requiredDocuments,
            supplierTax: cloneDeep(this.existingItemList[index]),
            taxDocuments: this.taxDocuments,
            taxCountries$: this.taxCountries$,
            taxIdTypes$: this.taxIdTypes$,
            settings$: this.settings$,
            taskStatus: this.taskStatus
          } 
        }
      );
      dialogRef.afterClosed().subscribe(res => {
        if(res) {
          res.ID = this.existingItemList[index].ID;
          res.IsPrimary = this.primaryIndex == index;
          res.Operation = this.existingItemList[index]?.Operation;
          this.existingItemList[index] = cloneDeep(res);
          this.reloadTableData();
          this.initEntityMappingOptionData();
        } else {
          if(event?.isFirstTime) {
            this.selectTableComponent?.onSelectSwitch(null, index, false);
          }
        }
      });
    }
  }

  onView(event) {
    let index = event?.index;
    if(index < this.existingItemList?.length) {
      const dialogRef = this.matDialog.open(TaxTaskDialogComponent,
        { 
          width: '1100px', 
          maxHeight: "80vh",
          autoFocus: false,
          data: { 
            supplierCode: this.supplierCode, 
            requiredDocuments: this.existingItemList[index]?.DocumentInfos?.filter(d => d.DocumentType && this.supplierTaxDocType.includes(d.DocumentType.toUpperCase())) || this.requiredDocuments,
            supplierTax: cloneDeep(this.existingItemList[index]),
            taxDocuments: this.taxDocuments,
            taxCountries$: this.taxCountries$,
            taxIdTypes$: this.taxIdTypes$,
            settings$: this.settings$,
            readOnly: true,
            taskStatus: this.taskStatus
          } 
        }
      );
    }
  }

  onAdd() {
    const dialogRef = this.matDialog.open(TaxTaskDialogComponent,
      { 
        width: '1100px', 
        maxHeight: "80vh",
        autoFocus: false,
        data: { 
          supplierCode: this.supplierCode, 
          requiredDocuments: this.requiredDocuments,
          supplierTax: new SupplierTax(),
          taxDocuments: this.taxDocuments,
          taxCountries$: this.taxCountries$,
          taxIdTypes$: this.taxIdTypes$,
          settings$: this.settings$,
          taskStatus: this.taskStatus
        } 
      }
    );
    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        res.ID = Math.floor(1000000 + Math.random() * 9000000);
        res.Operation = OPERATION.CREATE;
        this.selectedIndexList.push(this.existingItemList.push(cloneDeep(res)) - 1);
        this.reloadTableData();
        this.initEntityMappingOptionData();
      }
    })
  }

  onDelete(event) {
    let index = event?.index;
    this.selectedIndexList.splice(this.selectedIndexList.indexOf(index), 1);
    this.existingItemList.splice(index, 1);
    this.reloadTableData();
    this.initEntityMappingOptionData();
  }

  onFileUpload(event) {
    if(event) {
      this.existingItemList[event.index].DocumentInfos[event.documentIndex] = event.file;
    }
  }

  reloadTableData() {
    this.selectTableComponent?.updateTableData(this.existingItemList?.map((x, i) => {
      let obj = {
        taxCountry: x.TaxCountry,
        taxName: x.TaxName,
        taxType: x.TaxIDTypes,
        federalTaxID: !isNullOrUndefined(x.Last4FederalTaxID) && !isNullOrEmpty(x.Last4FederalTaxID) ? 'xxxx-'+x.Last4FederalTaxID : x.FederalTaxID,
        vatNumber: x.VATNumber,
        document: x.Documents?.join(", "),
        value: i == this.primaryIndex,
        isPrimary: x.IsPrimary,
        documentInfos: x.DocumentInfos?.length? x.DocumentInfos: [cloneDeep(this.requiredDocuments)],
        isNew: ["nameOrTINChange"].includes(this.requestType) ? x.Operation == OPERATION.CREATE : null
      };
      if(this.selectedIndexList?.includes(i)) {
        obj["isSelected"] = true;
        this.isValidSelectTable = true;
      } else {
        if(["nameOrTINChange"].includes(this.requestType)) {
          if(this.unSelectedIndexList?.includes(i)) obj["isSelected"] = false;
        } else if(this.selectedIndexList?.length || (!!(this.supplierTaxList?.length  && !this.selectedIndexList?.length))) obj["isSelected"] = false;
      }
      return obj;
    }), !["nameOrTINChange"].includes(this.requestType));
  }

  /** Triggers onSubmit action from child component */
  onSubmit() {
    if(["nameOrTINChange"].includes(this.requestType)) {
      if(!this.selectedIndexList?.length) {
        this.isValidSelectTable = false; 
        this.tableErrorMessage = "Please confirm at least one active tax above or add new information."
        return;
      }
      if(this.selectedIndexList?.length + this.unSelectedIndexList?.length != this.existingItemList?.length) {
        this.isValidSelectTable = false; 
        this.tableErrorMessage = "Please make decision for all items."
        return;
      }
      let entityMapping = this.entityMappingComponent?.onSubmit();
      if(!entityMapping?.length) return;
      let submitList = this.existingItemList?.filter((x, index) => this.selectedIndexList.findIndex(x => x == index) >= 0)?.map((item, index) => {
        item.EntityCountryIDList = entityMapping.filter(x => x.taskItem == item.ID)?.map(x => x.entityCountryID);
        if (item.Operation != OPERATION.CREATE) item.Operation = OPERATION.UPDATE;
        else delete item.ID;
        item.DocumentInfos = item.DocumentInfos?.filter(x => (x.DocumentID || x.File));
        item.Documents = item.DocumentInfos?.filter(x => x.DocumentID)?.map(x => x.DocumentID);
        return item;
      });
      this.taxFormSubmit(null, submitList);
    } else {
      if((this.isReactivation || this.enableExistSelection)) {
        if(this.existingItemList?.length && !this.selectedIndexList?.length) {
          this.isValidSelectTable = false; 
          this.tableErrorMessage = "Please confirm at least one active tax above or add new information."
          return;
        }
        if(this.isReactivation && !this.isCreateNew && !this.primaryIndex && this.primaryIndex != 0){
          this.isValidSelectTable = false; 
          this.tableErrorMessage = "Please select at least one primary tax."
          return;
        }
        if(!this.isCreateNew) {
          let submitList = this.selectedIndexList.map(index => {
            let item = this.existingItemList[index];
            item.IsValid = true;
            if(index == this.primaryIndex) item.IsPrimary = true;
            else item.IsPrimary = false;
            item.DocumentInfos = item.DocumentInfos?.filter(x => (x.DocumentID || x.File));
            item.Documents = item.DocumentInfos?.filter(x => x.DocumentID)?.map(x => x.DocumentID);
        return item;
          })
          this.taxFormSubmit(null, submitList);
          return;
        }
      } 
      this.taxEditComponent.onSubmit();
    } 
  }

  /** Handler for when the child components submits tax info with valid data */
  taxFormSubmit(supplierTax: SupplierTax, supplierTaxList?: SupplierTax[]) {
    if (supplierTax || supplierTaxList?.length) {
      let body: SupplierTax | SupplierTax[];
      if(this.isReactivation || this.enableExistSelection || ["nameOrTINChange"].includes(this.requestType)) {
        if(!supplierTaxList?.length) {
          supplierTax.IsPrimary = true;
          supplierTax.IsValid = true;
          let notValidList = cloneDeep(this.existingItemList);
          notValidList.forEach(x => {
            x.IsValid = false;
            x.IsPrimary = false;
            x.DocumentInfos = [];
            x.Documents = [];
            x.Operation = OPERATION.DELETE;
            delete x.RequiredDocuments;
          });
          delete supplierTax.RequiredDocuments;
          if(this.isReactivation) body = [{ ...supplierTax }, ...notValidList];
          else body = { ...supplierTax };
        }
        else {
          let uploadFileObsList: Observable<any>[] = [];
          let uploadFileIndexList: number[] = [];
          supplierTaxList?.forEach((tax, index) => {
            if(tax.DocumentInfos?.length) {
              if(tax.DocumentInfos[0].File && !tax.DocumentInfos[0].DocumentID) {
                uploadFileObsList.push(this.fileUploadService.uploadFileToServer([tax.DocumentInfos[0].File], this.supplierCode));
                uploadFileIndexList.push(index);
              }
            }
          });
          if(uploadFileObsList.length) {
            forkJoin(uploadFileObsList).subscribe(files => {
              if(files?.length) {
                files.forEach((file, index) => {
                  let document = supplierTaxList[uploadFileIndexList[index]].DocumentInfos[0];
                  document.DocumentID = file['documentID'] || file.DocumentID;
                  document.FileName = file['fileName'] || file.FileName;
                  document.DocumentVersion = file['documentVersion'] || file.DocumentVersion;
                  document.UploadedDate = file['modifiedDate'] || file.ModifiedDate;
                  document.Mode = 'Upload';
                  document.DocumentType = document.DocumentType ?? file.DocumentType;
                  document.File = null;
                  supplierTaxList[uploadFileIndexList[index]].Documents = supplierTaxList[uploadFileIndexList[index]].DocumentInfos?.map(x => x.DocumentID);
                })
              }
              let notValidList = this.existingItemList?.filter((x, index) => !this.selectedIndexList.includes(index)) || [];
              notValidList.forEach(x => {
                x.IsValid = false;
                x.IsPrimary = false;
                x.DocumentInfos = [];
                x.Documents = [];
                x.Operation = OPERATION.DELETE;
                delete x.RequiredDocuments;
              });
              supplierTaxList?.forEach(x => delete x.RequiredDocuments);
              body = [...supplierTaxList, ...notValidList];
              var response: SupplierTaskResponse = {
                id: this.taskId,
                supplierCode: this.supplierCode,
                taskResponseJson: JSON.stringify(body)
              }
              this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
                if (res) {
                  this.toaster.success(this.translate.get('Submitted successfully!'));
                  if (this.isSidePanelMode) this.onSave.emit(true);
                  else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
                }
                else {
                  this.toaster.error(this.translate.get('Something went wrong!'));
                }
              });
            }, error => this.toaster.error(this.translate.get('Something went wrong!')))
            return;
          } 
          let notValidList = this.existingItemList?.filter((x, index) => !this.selectedIndexList.includes(index)) || [];
          notValidList.forEach(x => {
            x.IsValid = false;
            x.IsPrimary = false;
            x.DocumentInfos = [];
            x.Documents = [];
            x.Operation = OPERATION.DELETE;
            delete x.RequiredDocuments;
          });
          supplierTaxList?.forEach(x => delete x.RequiredDocuments);
          if(this.isReactivation || ["nameOrTINChange"].includes(this.requestType)) body = [...supplierTaxList, ...notValidList];
          else body = {...supplierTaxList[0]};
        }
      } else {
        delete supplierTax.RequiredDocuments;
        body = { ...supplierTax }
      };
      var response: SupplierTaskResponse = {
        id: this.taskId,
        supplierCode: this.supplierCode,
        taskResponseJson: JSON.stringify(body)
      }
      this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
        if (res) {
          this.toaster.success(this.translate.get('Submitted successfully!'));
          if (this.isSidePanelMode) this.onSave.emit(true);
          else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
        }
        else {
          this.toaster.error(this.translate.get('Something went wrong!'));
        }
      });
    }
  }

  setErrorMessage(message: string) {
    if (message) this.toaster.error((message && message.trim() != '') ? message : this.translate.get('Something went wrong!'))
  }

  navigateToHome() {
    this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
  }
}
